/**
 * Token utility functions for JWT validation and expiration checking
 */

/**
 * Decode JWT token to extract payload
 */
export const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param token - JWT token string
 * @returns true if token is expired or invalid, false if valid
 */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;

  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) {
      return true;
    }

    // exp is in seconds, Date.now() is in milliseconds
    const currentTime = Date.now() / 1000;
    
    // Add a 30-second buffer to handle any clock skew
    return decoded.exp < (currentTime + 30);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Get time remaining until token expires (in milliseconds)
 * Returns 0 if token is expired or invalid
 */
export const getTokenExpiryTime = (token: string | null): number => {
  if (!token) return 0;

  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) {
      return 0;
    }

    const currentTime = Date.now() / 1000;
    const timeRemaining = (decoded.exp - currentTime) * 1000;
    
    return timeRemaining > 0 ? timeRemaining : 0;
  } catch (error) {
    console.error('Error getting token expiry time:', error);
    return 0;
  }
};

export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem('refreshToken');
};

export const setRefreshToken = (token: string): void => {
  localStorage.setItem('refreshToken', token);
};

export const clearRefreshToken = (): void => {
  localStorage.removeItem('refreshToken');
};

/**
 * Remove token from localStorage
 */
export const clearToken = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
};

/**
 * Validate token and clear if expired
 * @returns true if token is valid, false if expired or invalid
 */
export const validateAndClearToken = (): boolean => {
  const token = localStorage.getItem('token');
  
  if (!token || isTokenExpired(token)) {
    clearToken();
    return false;
  }
  
  return true;
};
